import { z } from 'zod';
import { BaseEventSchema } from './Base';

export const LoginContactWrittenEventSchema = BaseEventSchema.extend({
  type: z.enum(['phone', 'email']),
});

export const VerificationCodeEventSchema = BaseEventSchema.extend({
  type: z.enum(['phone', 'email']),
  code: z.string(),
});

export const CartEventSchema = BaseEventSchema.extend({
  access: z.string().optional(),
  products: z.number(),
});

export const ClickEventSchema = BaseEventSchema.extend({
  button_text: z.string(),
  payment_methods_registered: z.number().optional(),
});

export const CreateAddressEventSchema = BaseEventSchema.extend({
  street: z.string(),
  street_number: z.string(),
  commune: z.string(),
  apartment: z.string(),
  instructions: z.string(),
});

export const SelectAddressEventSchema = BaseEventSchema.extend({
  commune: z.string().optional(),
  zone: z.enum(['ZONE_A', 'ZONE_B', 'ZONE_C', 'NO_ZONE']).optional(),
  delivery_type: z.enum(['PICK_UP', 'DELIVERY']),
});

export const SelectDeliveryDateEventSchema = BaseEventSchema.extend({
  express: z.boolean(),
  deliveries: z.number(),
});

export const SelectPaymentMethodEventSchema = BaseEventSchema.extend({
  payment_method_type: z.string(),
});

export const SearchEventSchema = BaseEventSchema.extend({
  search_string: z.string(),
  search_results: z.number(),
  search_load_more_iteration: z.number().optional(),
  search_perfect_results: z.number(),
  search_not_perfect_results: z.number(),
});

export const SelectInstallmentsEventSchema = BaseEventSchema.extend({
  quantity: z.number(),
});

export const PaymentAttemptEventSchema = BaseEventSchema.extend({
  payment_method_type: z.string(),
  charge_delivery_cost: z.boolean().optional(),
});

export const ClickNextEventSchema = BaseEventSchema.extend({
  location: z.enum(['login', 'signup_user_info', 'signup_source_info']),
});

export enum InteractionEventTypes {
  // Search
  Search = 'interaction.search',
  SearchLoadMore = 'interaction.search.load_more',
  // Checkout
  RedirectNewPaymentMethod = 'interaction.redirect_new_payment_method',
  SelectInstallments = 'interaction.select_installments',
  PaymentAttempt = 'interaction.payment.attempt',
  PaymentCancel = 'interaction.payment.cancel',
  // Cart
  CartOpen = 'interaction.cart.open',
  CartClose = 'interaction.cart.close',
  CartStartPayment = 'interaction.cart.got_to_checkout',
  // Session
  LoginContactWritten = 'interaction.session.login_input_written',
  LoginVerificationCodeWritten = 'interaction.session.verification_code_written',
  LoginResendCode = 'interaction.session.resend_verification_code',
  ClickLogout = 'interaction.session.click_logout',
  ClickConfirmLogout = 'interaction.session.click_confirm_logout',
  // Others
  CreateAddress = 'interaction.create_address',
  SelectAddress = 'interaction.select_address',
  SelectDeliveryDate = 'interaction.select_delivery_date',
  SelectPaymentMethod = 'interaction.select_payment_method',
  ClickNext = 'interaction.click_next',
}

export const InteractionEventTrackingSchemas = {
  // Search
  [InteractionEventTypes.Search]: SearchEventSchema,
  [InteractionEventTypes.SearchLoadMore]: SearchEventSchema,
  // Checkout
  [InteractionEventTypes.RedirectNewPaymentMethod]: ClickEventSchema,
  [InteractionEventTypes.SelectInstallments]: SelectInstallmentsEventSchema,
  [InteractionEventTypes.PaymentAttempt]: PaymentAttemptEventSchema,
  [InteractionEventTypes.PaymentCancel]: PaymentAttemptEventSchema,
  // Cart
  [InteractionEventTypes.CartOpen]: CartEventSchema,
  [InteractionEventTypes.CartClose]: CartEventSchema,
  [InteractionEventTypes.CartStartPayment]: BaseEventSchema,
  // Login
  [InteractionEventTypes.LoginContactWritten]: LoginContactWrittenEventSchema,
  [InteractionEventTypes.LoginVerificationCodeWritten]:
    VerificationCodeEventSchema,
  [InteractionEventTypes.LoginResendCode]: BaseEventSchema,
  [InteractionEventTypes.ClickLogout]: BaseEventSchema,
  [InteractionEventTypes.ClickConfirmLogout]: BaseEventSchema,
  // Others
  [InteractionEventTypes.CreateAddress]: CreateAddressEventSchema,
  [InteractionEventTypes.SelectAddress]: SelectAddressEventSchema,
  [InteractionEventTypes.SelectDeliveryDate]: SelectDeliveryDateEventSchema,
  [InteractionEventTypes.SelectPaymentMethod]: SelectPaymentMethodEventSchema,
  [InteractionEventTypes.ClickNext]: ClickNextEventSchema,
} as const;
